import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { createSubmissionsTableColumns } from '../../components/submissions-table/submissions-table-columns';
import { DEFAULT_SURVEY_SUBMISSIONS_ORDER } from '../../graphql/queries';
import {
  useDeleteSurveySubmission,
  useGetSurveyBatchById,
  useGetSurveySubmissions,
} from '../../hooks/use-surveys';
import {
  SurveyBatch,
  SurveyBatchesPagingResult,
  SurveySubmission,
  SurveySubmissionsPagingResult,
} from '../../types/survey.types';
import { PageHeader, PageHeaderSkeleton } from '@/components/core';
import { APP_PATHS } from '@/constant/app-paths';

export const BatchDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { batchId } = useParams<{ batchId: string }>();

  const { data: batchData, isLoading: isBatchLoading } = useGetSurveyBatchById(batchId);
  const batch: SurveyBatch | undefined = (
    batchData as { SurveyBatches?: SurveyBatchesPagingResult }
  )?.SurveyBatches?.items?.[0];

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_SURVEY_SUBMISSIONS_ORDER,
  });
  const [deleteTarget, setDeleteTarget] = useState<SurveySubmission | null>(null);

  const { data, isLoading, error } = useGetSurveySubmissions({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({ SurveyBatchId: batchId }, columnWhere),
    order,
  });
  const result = (data as { SurveySubmissions?: SurveySubmissionsPagingResult })?.SurveySubmissions;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteSurveySubmission, isPending: isDeleting } =
    useDeleteSurveySubmission();

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteSurveySubmission(deleteTarget.ItemId);
    setDeleteTarget(null);
  };

  const columns = createSubmissionsTableColumns({
    t,
    onView: (submission) => navigate(`${APP_PATHS.ALL_SURVEYS}/${batchId}/${submission.ItemId}`),
    onDelete: setDeleteTarget,
  });

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Not the generic "Survey" title while the batch's own title loads. */}
      {isBatchLoading ? (
        <PageHeaderSkeleton subtitle={false} />
      ) : (
        <PageHeader title={batch?.Title ?? t('SURVEY')} />
      )}

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <ConfirmationModal
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('DELETE_SUBMISSION')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
